import {
  EXTENSIONES_ARCHIVO,
  esquemaAbrirRevision,
  formatearRevision,
  LARGO_MAXIMO_MOTIVO,
  MIMES_ARCHIVO,
  nombreArchivoCanonico,
  seRedacta,
  TAMANO_MAXIMO_ARCHIVO,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import type { RevisionDetalle } from "../tipos.ts";
import { formatearTamano, leerComoBase64, mensajeDe, mimeDeArchivo } from "../utiles.ts";
import { Modal } from "./ModalFirma.tsx";
import { Aviso, Boton, Campo, CLASE_ENTRADA, SelectorDeContenido } from "./ui.tsx";

type Persona = { id: string; nombre: string; puesto: string };

/**
 * "Abrir nueva revisión": la Rev. N+1 sobre la vigente. Pide el motivo del cambio y, de entrada,
 * arranca con todo lo de la vigente en el editor; si no, se sube el archivo nuevo. La ruta parte
 * de la de la vigente. La vigente sigue vigente hasta que se apruebe esta.
 */
export function ModalAbrirRevision({
  documentoId,
  codigo,
  procesoCodigo,
  tipoCodigo,
  requiereAprobacion,
  conRevisor = true,
  vigente,
  yo,
  onCerrar,
  onAbierta,
}: {
  documentoId: string;
  codigo: string;
  procesoCodigo: string;
  tipoCodigo: string;
  /** False = informativo: sin ruta de firmas. */
  requiereAprobacion: boolean;
  /** Ruta corta (2026-10-29): false = el tipo va sin revisor, solo elaboró y aprobó. */
  conRevisor?: boolean | undefined;
  vigente: RevisionDetalle;
  yo: string;
  onCerrar: () => void;
  onAbierta: () => void;
}) {
  const trpc = useTRPC();
  const personas = useQuery(trpc.documentos.personasRuta.queryOptions());
  const cargan = useQuery(trpc.documentos.quienesCargan.queryOptions({ procesoCodigo }));
  // Quien elaboró parte de quien la abre; se puede nombrar a otra persona que cargue en el
  // proceso (2026-10-22): esa firma "Elaboró" y quien abre puede quedar de revisor.
  const [elaboro, setElaboro] = useState(yo);
  const deLaVigente = (etapa: "reviso" | "aprobo") => {
    const id = vigente.ruta.find((p) => p.etapa === etapa)?.usuario?.id ?? "";
    return id === yo ? "" : id; // quien abre elabora: no puede quedar revisando su propia revisión
  };
  const [motivo, setMotivo] = useState("");
  const [archivo, setArchivo] = useState<File | null>(null);
  // La nueva arranca con todo lo de la vigente en el editor, se haya redactado o sea un archivo;
  // subir otro archivo sigue ahí como opción.
  const puedeRedactar = seRedacta(tipoCodigo);
  const [redactarElegido, setRedactarElegido] = useState<boolean | null>(null);
  const { perfil } = useRouteContext({ from: "/app" });
  // Con el permiso temporal se viene a subir lo que ya existe: de entrada, archivo.
  const redactar = puedeRedactar && (redactarElegido ?? !perfil.usuario.cargaSinFirmas);
  const sinFirmas = perfil.usuario.cargaSinFirmas && requiereAprobacion && !redactar;
  const navigate = useNavigate();
  const [reviso, setReviso] = useState(deLaVigente("reviso"));
  const [aprobo, setAprobo] = useState(deLaVigente("aprobo"));
  const [errores, setErrores] = useState<Record<string, string>>({});
  const numero = vigente.numero + 1;
  // El archivo de la vigente no se pudo copiar (un escaneo): hay que decidir qué hacer.
  const [sinCopia, setSinCopia] = useState(false);
  const [copiada, setCopiada] = useState<{
    numero: number;
    archivo: string;
    avisos: string[];
  } | null>(null);

  const irAlEditor = () => {
    onAbierta();
    navigate({ to: "/documentos/$codigo/editar", params: { codigo } });
  };

  const abrir = useMutation(
    trpc.documentos.abrirRevision.mutationOptions({
      onSuccess: (r) => {
        queryClient.invalidateQueries();
        if (r.copia) {
          setCopiada({ numero: r.numero, ...r.copia });
          return;
        }
        if (redactar) irAlEditor();
        else onAbierta();
      },
      onError: (e) => {
        const mensaje = mensajeDe(e);
        setSinCopia(mensaje.startsWith("No se pudo copiar"));
        setErrores({ general: mensaje });
      },
    }),
  );

  async function enviar(e: FormEvent, enBlanco = false) {
    e.preventDefault();
    setErrores({});
    const mime = archivo ? mimeDeArchivo(archivo) : null;
    if (!redactar) {
      if (!archivo) {
        setErrores({ archivo: "Adjunta el archivo de la revisión nueva" });
        return;
      }
      if (!mime) {
        setErrores({ archivo: "Solo se aceptan Word, Excel o PDF" });
        return;
      }
      if (archivo.size > TAMANO_MAXIMO_ARCHIVO) {
        setErrores({ archivo: "El archivo supera los 25 MB" });
        return;
      }
    }
    const adjunto =
      !redactar && archivo && mime
        ? { nombre: archivo.name, mime, base64: await leerComoBase64(archivo) }
        : undefined;
    const datos = esquemaAbrirRevision.safeParse({
      documentoId,
      motivo,
      archivo: adjunto,
      enBlanco: enBlanco || undefined,
      ruta:
        requiereAprobacion && !sinFirmas
          ? { ...(conRevisor ? { reviso } : {}), aprobo }
          : undefined,
      elaboro: elaboro !== yo && !sinFirmas ? elaboro : undefined,
      sinFirmas: sinFirmas || undefined,
    });
    if (!datos.success) {
      const porCampo: Record<string, string> = {};
      for (const i of datos.error.issues) porCampo[i.path.join(".")] ??= i.message;
      setErrores(porCampo);
      return;
    }
    abrir.mutate(datos.data);
  }

  const mimeElegido = archivo ? mimeDeArchivo(archivo) : null;
  // Quien elaboró no se revisa ni se aprueba a sí mismo; quien abre sí puede revisar lo de otro.
  const lista = (personas.data ?? []).filter((p) => p.id !== elaboro);
  const elaboradores = cargan.data ?? [];
  function cambiarElaboro(id: string) {
    setElaboro(id);
    if (reviso === id) setReviso("");
    if (aprobo === id) setAprobo("");
  }

  if (copiada) {
    return (
      <Modal titulo={`Rev. ${formatearRevision(copiada.numero)} abierta`} onCerrar={irAlEditor}>
        <div className="space-y-4">
          <p className="text-[13px] text-texto-2">
            Se copió el contenido de <span className="font-semibold">{copiada.archivo}</span> al
            editor. Es una copia automática: revísala contra el original antes de mandarla a firmar.
          </p>
          {copiada.avisos.length > 0 && (
            <Aviso tono="ambar">
              <ul className="list-disc space-y-1 pl-4">
                {copiada.avisos.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </Aviso>
          )}
          <div className="flex justify-end pt-1">
            <Boton onClick={irAlEditor}>Ir al editor</Boton>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal titulo={`Abrir la Rev. ${formatearRevision(numero)}`} onCerrar={onCerrar}>
      <form onSubmit={(e) => void enviar(e)} className="space-y-4">
        <Aviso tono="ambar">
          {sinFirmas ? (
            <>
              Tienes el permiso temporal: la Rev. {formatearRevision(numero)} queda vigente de una
              vez, sin firmas, y la {formatearRevision(vigente.numero)} pasa a obsoleta.
            </>
          ) : (
            <>
              La Rev. {formatearRevision(vigente.numero)} sigue vigente mientras la{" "}
              {formatearRevision(numero)} pasa por las tres firmas. Al aprobarse, la{" "}
              {formatearRevision(vigente.numero)} queda obsoleta.
            </>
          )}
        </Aviso>

        <Campo
          etiqueta="Motivo del cambio"
          ayuda={`Queda en el historial y en la hoja de control del PDF. Hasta ${LARGO_MAXIMO_MOTIVO} caracteres${motivo.length > LARGO_MAXIMO_MOTIVO / 2 ? ` (llevas ${motivo.length})` : ""}.`}
          error={errores.motivo}
        >
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={4}
            placeholder="Qué cambia respecto de la revisión vigente"
            className={CLASE_ENTRADA}
          />
        </Campo>

        {puedeRedactar && <SelectorDeContenido redactar={redactar} onCambio={setRedactarElegido} />}
        {redactar ? (
          <p className="text-[12.5px] text-texto-3">
            {vigente.redactada
              ? `La Rev. ${formatearRevision(numero)} arranca como copia exacta de la vigente, para cambiar solo lo que cambia.`
              : `La Rev. ${formatearRevision(numero)} arranca con todo lo que dice el archivo de la vigente, ya pasado al editor con la plantilla de SMG, para cambiar solo lo que cambia.`}
          </p>
        ) : (
          <Campo
            etiqueta="Archivo de la revisión nueva"
            ayuda={
              archivo && mimeElegido
                ? `${formatearTamano(archivo.size)} · se guarda como ${nombreArchivoCanonico(codigo, numero, MIMES_ARCHIVO[mimeElegido])}`
                : "Word, Excel o PDF, hasta 25 MB."
            }
            error={errores.archivo}
          >
            <input
              type="file"
              accept={EXTENSIONES_ARCHIVO.map((x) => `.${x}`).join(",")}
              onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
              className={`${CLASE_ENTRADA} file:mr-3 file:rounded file:border-0 file:bg-marino-claro file:px-2 file:py-1 file:text-[12px] file:font-semibold file:text-marino`}
            />
          </Campo>
        )}

        {requiereAprobacion && !sinFirmas && (
          <>
            <Campo
              etiqueta="Elaboró"
              ayuda={
                elaboro === yo
                  ? "Si subes la revisión por otra persona, elígela: ella firma como Elaboró y tú puedes revisarla."
                  : "Le llegará el aviso para firmar como Elaboró; mientras, tú también puedes editar el borrador."
              }
            >
              <select
                value={elaboro}
                onChange={(e) => cambiarElaboro(e.target.value)}
                className={CLASE_ENTRADA}
              >
                <option value={yo}>Yo</option>
                {elaboradores
                  .filter((p) => p.id !== yo)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} · {p.puesto}
                    </option>
                  ))}
              </select>
            </Campo>
            <SelectoresRuta
              personas={lista}
              reviso={reviso}
              aprobo={aprobo}
              onReviso={setReviso}
              onAprobo={setAprobo}
              conRevisor={conRevisor}
              errores={errores}
            />
          </>
        )}

        {errores.general && <Aviso>{errores.general}</Aviso>}

        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={abrir.isPending}>
            Cancelar
          </Boton>
          {sinCopia && redactar && (
            <Boton
              variante="secundario"
              disabled={abrir.isPending}
              onClick={(e) => void enviar(e, true)}
            >
              Empezar en blanco
            </Boton>
          )}
          <Boton type="submit" disabled={abrir.isPending}>
            {abrir.isPending
              ? "Abriendo…"
              : `${sinFirmas ? "Publicar" : "Abrir"} Rev. ${formatearRevision(numero)}`}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

/**
 * "Cambiar revisor o aprobador". En borrador lo hace quien elabora; en curso, un administrador. Una
 * etapa que ya firmó en esta ronda queda fija.
 */
export function ModalCambiarRuta({
  revision,
  onCerrar,
  onCambiada,
}: {
  revision: RevisionDetalle;
  onCerrar: () => void;
  onCambiada: () => void;
}) {
  const trpc = useTRPC();
  const personas = useQuery(trpc.documentos.personasRuta.queryOptions());
  const actual = (etapa: "elaboro" | "reviso" | "aprobo") =>
    revision.ruta.find((p) => p.etapa === etapa)?.usuario?.id ?? "";
  const [reviso, setReviso] = useState(actual("reviso"));
  const [aprobo, setAprobo] = useState(actual("aprobo"));
  const [error, setError] = useState<string | null>(null);
  // Ruta corta: la ruta se armó sin revisor y así se queda.
  const conRevisor = revision.ruta.some((p) => p.etapa === "reviso" && p.usuario);
  // Si ya pasó la revisión, quien revisó firmó: su lugar no se toca.
  const revisoFijo = revision.estado === "esperando_aprobacion";

  const cambiar = useMutation(
    trpc.documentos.cambiarRuta.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries();
        onCambiada();
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    cambiar.mutate({ revisionId: revision.id, ...(conRevisor ? { reviso } : {}), aprobo });
  }

  const elabora = actual("elaboro");
  const lista = (personas.data ?? []).filter((p) => p.id !== elabora);
  const sinCambios = (!conRevisor || reviso === actual("reviso")) && aprobo === actual("aprobo");

  return (
    <Modal
      titulo={`Ruta de aprobación de la Rev. ${formatearRevision(revision.numero)}`}
      onCerrar={onCerrar}
    >
      <form onSubmit={enviar} className="space-y-4">
        <p className="text-[12.5px] text-texto-3">
          Quien elabora no cambia. El cambio queda en auditoría con quién estaba antes y quién queda
          ahora.
        </p>
        <SelectoresRuta
          personas={lista}
          reviso={reviso}
          aprobo={aprobo}
          onReviso={setReviso}
          onAprobo={setAprobo}
          conRevisor={conRevisor}
          revisoFijo={revisoFijo}
          errores={{}}
        />
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={cambiar.isPending}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={cambiar.isPending || sinCambios}>
            {cambiar.isPending ? "Guardando…" : "Guardar ruta"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

function SelectoresRuta({
  personas,
  reviso,
  aprobo,
  onReviso,
  onAprobo,
  conRevisor = true,
  revisoFijo = false,
  errores,
}: {
  personas: Persona[];
  reviso: string;
  aprobo: string;
  onReviso: (id: string) => void;
  onAprobo: (id: string) => void;
  /** Ruta corta: sin el selector de revisor. */
  conRevisor?: boolean;
  revisoFijo?: boolean;
  errores: Record<string, string>;
}) {
  const opciones = (vacio: string) => (
    <>
      <option value="">{vacio}</option>
      {personas.map((p) => (
        <option key={p.id} value={p.id}>
          {p.nombre} · {p.puesto}
        </option>
      ))}
    </>
  );
  return (
    <div className={`grid gap-3 ${conRevisor ? "sm:grid-cols-2" : ""}`}>
      {conRevisor && (
        <Campo
          etiqueta="Revisó"
          ayuda={revisoFijo ? "Ya firmó esta revisión: no se reemplaza." : undefined}
          error={errores["ruta.reviso"]}
        >
          <select
            value={reviso}
            onChange={(e) => onReviso(e.target.value)}
            disabled={revisoFijo}
            className={CLASE_ENTRADA}
          >
            {opciones("Elige quién revisa…")}
          </select>
        </Campo>
      )}
      <Campo etiqueta="Aprobó" error={errores["ruta.aprobo"]}>
        <select value={aprobo} onChange={(e) => onAprobo(e.target.value)} className={CLASE_ENTRADA}>
          {opciones("Elige quién aprueba…")}
        </select>
      </Campo>
    </div>
  );
}

/**
 * Fase 6 · "Retirar documento": un administrador lo saca del sistema de gestión. La vigente pasa a
 * obsoleta y nada se borra; el motivo queda en la auditoría y le llega al dueño del proceso.
 */
export function ModalRetirar({
  codigo,
  nombre,
  numero,
  onCerrar,
  onRetirado,
}: {
  codigo: string;
  nombre: string;
  numero: number;
  onCerrar: () => void;
  onRetirado: () => void;
}) {
  const trpc = useTRPC();
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const retirar = useMutation(
    trpc.documentos.retirar.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries();
        onRetirado();
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    retirar.mutate({ codigo, motivo });
  }

  return (
    <Modal titulo={`Retirar ${codigo}`} onCerrar={onCerrar} bloqueado={retirar.isPending}>
      <form onSubmit={enviar} className="space-y-4">
        <p className="text-[12.5px] text-texto-3">
          «{nombre}» deja de ser un documento vigente del sistema de gestión: su Rev.{" "}
          {formatearRevision(numero)} pasa a obsoleta y no se abren más revisiones. Nada se borra:
          las revisiones, las firmas y los acuses quedan para consulta. El dueño del proceso y quien
          elaboró la revisión reciben un aviso con el motivo.
        </p>
        <Campo etiqueta="Motivo" ayuda="Por qué se retira. Queda en la auditoría.">
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={4}
            className={CLASE_ENTRADA}
            // biome-ignore lint/a11y/noAutofocus: el modal se abre para escribir aquí
            autoFocus
          />
        </Campo>
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={retirar.isPending}>
            Cancelar
          </Boton>
          <Boton variante="peligro" type="submit" disabled={retirar.isPending}>
            {retirar.isPending ? "Retirando…" : "Retirar documento"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Corregir el nombre de una vigente subida sin firmas (permiso temporal, 2026-11-03): un nombre
 * mal escrito al cargar no tiene borrador donde cambiarse. El código no cambia.
 */
export function ModalCorregirNombre({
  revisionId,
  codigo,
  nombre: actual,
  onCerrar,
  onCorregido,
}: {
  /** La vigente. */
  revisionId: string;
  codigo: string;
  nombre: string;
  onCerrar: () => void;
  onCorregido: () => void;
}) {
  const trpc = useTRPC();
  const [nombre, setNombre] = useState(actual);
  const [error, setError] = useState<string | null>(null);
  const corregir = useMutation(
    trpc.documentos.corregirNombre.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries();
        onCorregido();
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    corregir.mutate({ revisionId, nombre });
  }

  return (
    <Modal titulo={`Nombre de ${codigo}`} onCerrar={onCerrar} bloqueado={corregir.isPending}>
      <form onSubmit={enviar} className="space-y-4">
        <Aviso tono="ambar">
          Tienes el permiso temporal: el nombre cambia ya en la revisión vigente, sin abrir una
          nueva, y queda en la auditoría. El código {codigo} no cambia.
        </Aviso>
        <Campo etiqueta="Nombre del documento">
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className={CLASE_ENTRADA}
            // biome-ignore lint/a11y/noAutofocus: el modal se abre para escribir aquí
            autoFocus
          />
        </Campo>
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={corregir.isPending}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={corregir.isPending || nombre.trim() === actual}>
            {corregir.isPending ? "Guardando…" : "Guardar"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 2026-10-20 · Comunicados: a toda la empresa o solo a las personas elegidas
// ---------------------------------------------------------------------------

type PersonaElegible = { id: string; nombre: string; puesto: string | null };

/**
 * "¿A quién va?" de un comunicado: toda la empresa, o una lista de personas con buscador. Lo usa
 * la pantalla 02 al crear y el modal "Cambiar destinatarios" de la 03.
 */
export function SelectorDestinatarios({
  personas,
  alcance,
  onAlcance,
  elegidos,
  onElegidos,
  error,
}: {
  personas: PersonaElegible[];
  alcance: "todos" | "personas";
  onAlcance: (a: "todos" | "personas") => void;
  elegidos: string[];
  onElegidos: (ids: string[]) => void;
  error?: string | undefined;
}) {
  const [filtro, setFiltro] = useState("");
  const texto = filtro.trim().toLowerCase();
  const visibles = texto
    ? personas.filter(
        (p) =>
          p.nombre.toLowerCase().includes(texto) || (p.puesto ?? "").toLowerCase().includes(texto),
      )
    : personas;
  const alternar = (id: string) =>
    onElegidos(elegidos.includes(id) ? elegidos.filter((x) => x !== id) : [...elegidos, id]);

  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-texto-3">
        A quién va
      </legend>
      <label className="flex items-start gap-2 text-[13px]">
        <input
          type="radio"
          name="alcance"
          checked={alcance === "todos"}
          onChange={() => onAlcance("todos")}
          className="mt-0.5"
        />
        <span>
          <span className="font-semibold">A toda la empresa.</span> Lo ve y lo recibe todo el
          personal activo.
        </span>
      </label>
      <label className="flex items-start gap-2 text-[13px]">
        <input
          type="radio"
          name="alcance"
          checked={alcance === "personas"}
          onChange={() => onAlcance("personas")}
          className="mt-0.5"
        />
        <span>
          <span className="font-semibold">Solo a estas personas.</span> El personal del proceso lo
          sigue viendo; nadie más.
        </span>
      </label>
      {alcance === "personas" && (
        <div className="rounded-md border border-borde bg-white p-2">
          <input
            type="search"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar por nombre o puesto"
            className={CLASE_ENTRADA}
          />
          <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
            {visibles.map((p) => (
              <li key={p.id}>
                <label className="flex items-start gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={elegidos.includes(p.id)}
                    onChange={() => alternar(p.id)}
                    className="mt-0.5"
                  />
                  <span>
                    {p.nombre}
                    {p.puesto && (
                      <span className="block text-[11.5px] text-texto-3">{p.puesto}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
            {visibles.length === 0 && (
              <li className="px-1 py-2 text-[12.5px] text-texto-3">
                Nadie coincide con la búsqueda.
              </li>
            )}
          </ul>
          <div className="mt-2 text-[11.5px] text-texto-3">
            {elegidos.length === 0
              ? "Nadie elegido todavía."
              : elegidos.length === 1
                ? "1 persona elegida."
                : `${elegidos.length} personas elegidas.`}
          </div>
        </div>
      )}
      {error && <Aviso>{error}</Aviso>}
    </fieldset>
  );
}

/** Pantalla 03 · "Cambiar destinatarios" de un comunicado. Lista vacía = toda la empresa. */
export function ModalCambiarDestinatarios({
  documentoId,
  actuales,
  onCerrar,
  onCambiados,
}: {
  documentoId: string;
  actuales: string[];
  onCerrar: () => void;
  onCambiados: () => void;
}) {
  const trpc = useTRPC();
  const personas = useQuery(trpc.documentos.personasRuta.queryOptions());
  const [alcance, setAlcance] = useState<"todos" | "personas">(
    actuales.length > 0 ? "personas" : "todos",
  );
  const [elegidos, setElegidos] = useState<string[]>(actuales);
  const [error, setError] = useState<string | null>(null);
  const cambiar = useMutation(
    trpc.documentos.cambiarDestinatarios.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries();
        onCambiados();
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (alcance === "personas" && elegidos.length === 0) {
      setError("Elige al menos a una persona, o manda el comunicado a todos");
      return;
    }
    cambiar.mutate({ documentoId, destinatarios: alcance === "personas" ? elegidos : [] });
  }

  return (
    <Modal titulo="A quién va este comunicado" onCerrar={onCerrar}>
      <form onSubmit={enviar} className="space-y-4">
        <p className="text-[12.5px] text-texto-3">
          Si ya se publicó, a quien entra ahora no se le vuelve a avisar: lo ve y da acuse desde
          esta lista. El cambio queda en auditoría.
        </p>
        <SelectorDestinatarios
          personas={personas.data ?? []}
          alcance={alcance}
          onAlcance={setAlcance}
          elegidos={elegidos}
          onElegidos={setElegidos}
        />
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={cambiar.isPending}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={cambiar.isPending}>
            {cambiar.isPending ? "Guardando…" : "Guardar destinatarios"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}
